"use client";

import { type ReactNode } from "react";
import { E_IN, useInView, useReducedMotion } from "@/lib/motion";
import { useIsMobile } from "@/lib/use-is-mobile";

/* Generic fade-up on scroll. Wrap any block; it animates in once.
 *
 * On mobile / reduced-motion, the wrapper renders fully visible from
 * mount — no IntersectionObserver, no opacity flicker. CSS in
 * globals.css collapses any leftover transitions to 0s so even if
 * something slips through it doesn't animate. */
export function Reveal({
  children,
  delay = 0,
  duration = 700,
  y = 12,
  className = "",
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  y?: number;
  className?: string;
}) {
  const rm = useReducedMotion();
  const isMobile = useIsMobile();
  const [ref, seen] = useInView<HTMLDivElement>();
  const skipMotion = rm || isMobile;
  const visible = skipMotion || seen;

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : `translateY(${y}px)`,
        transition: skipMotion
          ? "none"
          : `opacity ${duration}ms ${E_IN} ${delay}ms, transform ${duration}ms ${E_IN} ${delay}ms`,
        willChange: skipMotion ? undefined : "opacity, transform",
      }}
    >
      {children}
    </div>
  );
}
